import { jsx, jsxs } from 'react/jsx-runtime';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import 'date-fns-tz';
import 'clsx';
import 'tailwind-merge';
import { Label } from '../ui/label.js';
import '../ui/button.js';
import '@radix-ui/react-progress';
import '@clerk/clerk-react';
import { Plus, GripVertical, X } from 'lucide-react';
import { useState, useMemo, useEffect, useCallback } from 'react';
import '../../lib/toast.js';
import 'react-router-dom';
import { Button } from '../page-layout/Button.js';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import '../page-layout/FormPageLayout.js';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command.js';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover.js';
import '../form/RichTextEditor.js';
import '@radix-ui/react-switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog.js';
import './CrudDetailView.js';
import './CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '../ui/badge.js';
import 'react-day-picker';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import { Input } from '../ui/input.js';
import 'vaul';
import '@radix-ui/react-dropdown-menu';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '@radix-ui/react-dialog';
import '../ui/sidebar.js';
import 'react-toastify';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';

function SaveListDialog({ open, onOpenChange, config, currentFilters, currentSortField, currentSortOrder, editingView, onSave, onUpdate, }) {
    const [name, setName] = useState('');
    const [scope, setScope] = useState('personal');
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [saving, setSaving] = useState(false);
    const [addColumnOpen, setAddColumnOpen] = useState(false);
    // Get available fields from config that can be columns
    const availableFields = config.fields.filter(f => f.type !== 'custom' || f.format);
    // Build a map from field key to label for quick lookup
    const fieldLabelMap = useMemo(() => {
        const map = {};
        for (const f of availableFields) {
            map[f.key] = f.label;
        }
        return map;
    }, [availableFields]);
    // Get default column keys from listView config
    const defaultColumnKeys = getDefaultColumnKeys(config);
    // Columns not yet selected (available to add)
    const unselectedFields = useMemo(() => availableFields.filter(f => !selectedColumns.includes(f.key)), [availableFields, selectedColumns]);
    // Initialize state when dialog opens or editingView changes
    useEffect(() => {
        if (open) {
            if (editingView) {
                setName(editingView.name);
                setScope(editingView.scope);
                setSelectedColumns(editingView.columns);
            }
            else {
                setName('');
                setScope('personal');
                setSelectedColumns(defaultColumnKeys);
            }
        }
    }, [open, editingView]);
    // Drag and drop sensors
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setSelectedColumns(prev => {
                const oldIndex = prev.indexOf(active.id);
                const newIndex = prev.indexOf(over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    }, []);
    const handleRemoveColumn = useCallback((fieldKey) => {
        setSelectedColumns(prev => prev.filter(k => k !== fieldKey));
    }, []);
    const handleAddColumn = useCallback((fieldKey) => {
        setSelectedColumns(prev => [...prev, fieldKey]);
        setAddColumnOpen(false);
    }, []);
    const handleSave = async () => {
        if (!name.trim() || selectedColumns.length === 0)
            return;
        setSaving(true);
        try {
            if (editingView && onUpdate) {
                await onUpdate({
                    ...editingView,
                    name: name.trim(),
                    scope,
                    columns: selectedColumns,
                    filters: currentFilters,
                    sortField: currentSortField,
                    sortOrder: currentSortOrder,
                });
            }
            else {
                await onSave({
                    name: name.trim(),
                    scope,
                    columns: selectedColumns,
                    filters: currentFilters,
                    sortField: currentSortField,
                    sortOrder: currentSortOrder,
                });
            }
            onOpenChange(false);
        }
        finally {
            setSaving(false);
        }
    };
    return (jsx(Dialog, { open: open, onOpenChange: onOpenChange, children: jsxs(DialogContent, { className: "sm:max-w-[480px]", children: [jsxs(DialogHeader, { children: [jsx(DialogTitle, { children: editingView ? 'Edit Saved View' : 'Save Current View' }), jsx(DialogDescription, { children: editingView
                                ? 'Update the name, scope, and columns for this view.'
                                : 'Save the current filters and column selection as a named view.' })] }), jsxs("div", { className: "space-y-4 py-2", children: [jsxs("div", { className: "space-y-1.5", children: [jsx(Label, { htmlFor: "view-name", children: "Name" }), jsx(Input, { id: "view-name", value: name, onChange: e => setName(e.target.value), placeholder: "e.g., Published in Charleston", autoFocus: true })] }), jsxs("div", { className: "space-y-1.5", children: [jsx(Label, { children: "Visibility" }), jsxs(RadioGroup, { value: scope, onValueChange: v => setScope(v), className: "flex gap-4", children: [jsxs("div", { className: "flex items-center gap-2", children: [jsx(RadioGroupItem, { value: "personal", id: "scope-personal" }), jsx(Label, { htmlFor: "scope-personal", className: "font-normal cursor-pointer", children: "Personal" })] }), jsxs("div", { className: "flex items-center gap-2", children: [jsx(RadioGroupItem, { value: "shared", id: "scope-shared" }), jsx(Label, { htmlFor: "scope-shared", className: "font-normal cursor-pointer", children: "Shared" })] })] })] }), jsxs("div", { className: "space-y-1.5", children: [jsx(Label, { children: "Columns" }), jsx("div", { className: "border rounded-lg max-h-[240px] overflow-y-auto", children: selectedColumns.length === 0 ? (jsx("div", { className: "p-4 text-sm text-muted-foreground text-center", children: "No columns selected. Add columns below." })) : (jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: jsx(SortableContext, { items: selectedColumns, strategy: verticalListSortingStrategy, children: jsx("div", { className: "p-1 space-y-0.5", children: selectedColumns.map(key => (jsx(SortableColumnItem, { id: key, label: fieldLabelMap[key] || key, onRemove: () => handleRemoveColumn(key), canRemove: selectedColumns.length > 1 }, key))) }) }) })) }), jsxs("div", { className: "flex items-center justify-between", children: [jsxs("p", { className: "text-xs text-muted-foreground", children: [selectedColumns.length, " column", selectedColumns.length !== 1 ? 's' : '', " selected"] }), unselectedFields.length > 0 && (jsxs(Popover, { open: addColumnOpen, onOpenChange: setAddColumnOpen, children: [jsx(PopoverTrigger, { asChild: true, children: jsxs(Button, { variant: "outline", size: "sm", className: "h-7 gap-1 text-xs", children: [jsx(Plus, { className: "h-3 w-3" }), "Add Column"] }) }), jsx(PopoverContent, { className: "w-[220px] p-0", align: "end", children: jsxs(Command, { children: [jsx(CommandInput, { placeholder: "Search columns..." }), jsxs(CommandList, { children: [jsx(CommandEmpty, { children: "No columns found." }), jsx(CommandGroup, { children: unselectedFields.map(field => (jsx(CommandItem, { onSelect: () => handleAddColumn(field.key), children: field.label }, field.key))) })] })] }) })] }))] })] })] }), jsx(DialogFooter, { children: jsxs("div", { className: "flex flex-col-reverse sm:flex-row sm:justify-end gap-3", children: [jsx(Button, { variant: "outline", onClick: () => onOpenChange(false), children: "Cancel" }), jsx(Button, { onClick: handleSave, disabled: !name.trim() || selectedColumns.length === 0 || saving, loading: saving, children: editingView ? 'Update View' : 'Save View' })] }) })] }) }));
}
function SortableColumnItem({ id, label, onRemove, canRemove, }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (jsxs("div", { ref: setNodeRef, style: style, className: "flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted/50 group", children: [jsx("div", { ...attributes, ...listeners, className: "cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground shrink-0", children: jsx(GripVertical, { className: "h-4 w-4" }) }), jsx("span", { className: "text-sm flex-1 truncate", children: label }), canRemove && (jsx("button", { type: "button", onClick: onRemove, className: "p-0.5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0", children: jsx(X, { className: "h-3.5 w-3.5" }) }))] }));
}
function getDefaultColumnKeys(config) {
    if (config.listView.columns) {
        return config.listView.columns.map(c => c.field);
    }
    if (config.listView.fields) {
        return config.listView.fields;
    }
    return [];
}

export { SaveListDialog };
//# sourceMappingURL=SaveListDialog.js.map
